import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Check, Search, Plus, Minus, Loader2, AlertCircle, CheckCircle2, Clock, Stethoscope } from 'lucide-react';
import { format, addMinutes, differenceInMinutes } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Cie11Search, type Cie11Result } from '@/components/cie11/Cie11Search';
import { getErrorMessage } from '@/lib/errors';

/* ── Types ─────────────────────────────────────────────────────────────── */

type Step = 1 | 2 | 3 | 4;

type Recurrencia = 'semanal' | 'quincenal' | 'mensual' | 'trimestral';

interface WizardState {
  fecha: string;
  hora: string;
  medicoId: string;
  clinicaId: string;
  pacienteId: string;
  cie11Codigo?: string;
  cie11Titulo?: string;
  duracion: number;
  tipo: 'presencial' | 'teleconsulta';
  motivo: string;
  esRecurrente: boolean;
  recurrencia: Recurrencia | '';
  notas: string;
  servicioId?: string;
  servicioNombre?: string;
}

interface CitaFormValues {
  id?: string;
  pacienteId: string;
  medicoId: string;
  clinicaId: string;
  fechaInicio: string;
  fechaFin: string;
  tipo: string;
  motivo: string;
  codigoCie11?: string;
  tituloCie11?: string;
  esRecurrente: boolean;
  recurrencia?: string;
  notas?: string;
  servicioId?: string;
  servicioNombre?: string;
}

export interface CitaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pacientes: any[];
  medicos: any[];
  clinicas: any[];
  defaultValues?: Partial<CitaFormValues>;
  editingCita?: any;
  onSubmit: (data: CitaFormValues) => void;
  isSubmitting?: boolean;
}

/* ── Constants ─────────────────────────────────────────────────────────── */

const TIME_SLOTS: string[] = [];
for (let h = 6; h <= 22; h++) {
  for (const m of [0, 30]) {
    if (h === 22 && m === 30) break;
    TIME_SLOTS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

const INIT: WizardState = {
  fecha: format(new Date(), 'yyyy-MM-dd'),
  hora: '09:00',
  medicoId: '',
  clinicaId: '',
  pacienteId: '',
  cie11Codigo: undefined,
  cie11Titulo: undefined,
  duracion: 30,
  tipo: 'presencial',
  motivo: '',
  esRecurrente: false,
  recurrencia: '',
  notas: '',
  servicioId: undefined,
  servicioNombre: undefined,
};

const RECURRENCIA_OPTS: { value: Recurrencia; label: string; desc: string }[] = [
  { value: 'semanal',    label: 'Semanal',    desc: 'Cada 7 días' },
  { value: 'quincenal',  label: 'Quincenal',  desc: 'Cada 2 semanas' },
  { value: 'mensual',    label: 'Mensual',    desc: 'Mismo día cada mes' },
  { value: 'trimestral', label: 'Trimestral', desc: 'Cada 3 meses' },
];

const STEPS_META = [
  { n: 1 as const, label: 'Fecha y sala' },
  { n: 2 as const, label: 'Paciente' },
  { n: 3 as const, label: 'Tratamiento', optional: true },
  { n: 4 as const, label: 'Resumen' },
];

/* ── Availability types ─────────────────────────────────────────────────── */

interface AvailabilityStatus {
  loading: boolean;
  clinicaConfigured: boolean;
  clinicaOpen: boolean;
  medicoConfigured: boolean;
  medicoAvailable: boolean;
  hasConflict: boolean;
}

const ES_DAYS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'] as const;

function timeToMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

/* ── Helpers ────────────────────────────────────────────────────────────── */

function initials(a: string, b: string) {
  return `${a[0] ?? ''}${b[0] ?? ''}`.toUpperCase();
}

function snapHora(h: string): string {
  const [hh, mm] = h.split(':').map(Number);
  const snapped = mm >= 30 ? `${String(hh).padStart(2, '0')}:30` : `${String(hh).padStart(2, '0')}:00`;
  return TIME_SLOTS.includes(snapped) ? snapped : '09:00';
}

function parseDefaults(dv?: Partial<CitaFormValues>): WizardState {
  const s = { ...INIT };
  if (dv?.fechaInicio) {
    const d = new Date(dv.fechaInicio);
    s.fecha = format(d, 'yyyy-MM-dd');
    s.hora = snapHora(format(d, 'HH:mm'));
    if (dv.fechaFin) {
      const dur = differenceInMinutes(new Date(dv.fechaFin), d);
      s.duracion = Math.max(15, Math.round(dur / 15) * 15);
    }
  }
  return s;
}

function parseCita(cita: any): WizardState {
  const fi = new Date(cita.fechaInicio);
  const ff = new Date(cita.fechaFin);
  return {
    fecha: format(fi, 'yyyy-MM-dd'),
    hora: snapHora(format(fi, 'HH:mm')),
    medicoId: cita.medicoId ?? '',
    clinicaId: cita.clinicaId ?? '',
    pacienteId: cita.pacienteId ?? '',
    cie11Codigo: cita.codigoCie11 ?? undefined,
    cie11Titulo: cita.tituloCie11 ?? undefined,
    duracion: Math.max(15, Math.round(differenceInMinutes(ff, fi) / 15) * 15),
    tipo: cita.tipo ?? 'presencial',
    motivo: cita.motivo ?? '',
    esRecurrente: cita.esRecurrente ?? false,
    recurrencia: (cita.recurrencia as Recurrencia) ?? '',
    notas: cita.notas ?? '',
    servicioId: cita.servicioId ?? undefined,
    servicioNombre: cita.servicioNombre ?? undefined,
  };
}

const FIELD_CLS =
  'w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';

/* ── Step Indicator ─────────────────────────────────────────────────────── */

function StepIndicator({
  current, onGoTo,
}: { current: Step; onGoTo: (s: Step) => void }) {
  return (
    <div className="grid grid-cols-4 gap-1.5 mb-5">
      {STEPS_META.map(({ n, label, optional }) => {
        const done = current > n;
        const active = current === n;
        return (
          <button
            key={n}
            type="button"
            onClick={() => done ? onGoTo(n) : undefined}
            disabled={!done}
            className={cn(
              'text-left px-2.5 py-2 rounded-xl border transition-all',
              active && 'border-primary-500 bg-primary-50',
              done && 'border-primary-200 bg-primary-50/50 hover:bg-primary-50 cursor-pointer',
              !active && !done && 'border-gray-200 bg-white cursor-default',
            )}
          >
            <p className={cn(
              'text-[9px] font-bold uppercase tracking-wider mb-0.5 leading-none',
              active ? 'text-primary-600' : done ? 'text-primary-500' : 'text-text-secondary',
            )}>
              {done ? '✓ PASO ' : 'PASO '}{n}
              {optional && !active && !done ? ' · OPC.' : ''}
            </p>
            <p className={cn(
              'text-[11px] font-semibold leading-tight mt-0.5',
              active ? 'text-primary-700' : done ? 'text-primary-600' : 'text-text-secondary',
            )}>
              {label}
            </p>
          </button>
        );
      })}
    </div>
  );
}

/* ── Time slot status ───────────────────────────────────────────────────── */

type SlotStatus = 'open' | 'closed' | 'conflict';

function computeSlotStatuses(
  fecha: string,
  duracion: number,
  clinicaId: string,
  medicoId: string,
  clinicaHorarios: any[],
  bloquesMedico: any[],
  citasDelDia: any[],
  editingId?: string,
): Map<string, SlotStatus> {
  const map = new Map<string, SlotStatus>();
  if (!fecha || !clinicaId) return map;

  const now          = new Date();
  const selectedDate = new Date(`${fecha}T12:00:00`);
  const isToday      = selectedDate.toDateString() === now.toDateString();
  const nowMins      = now.getHours() * 60 + now.getMinutes();

  const diaSemana = ES_DAYS[selectedDate.getDay()];
  const dayClinica = clinicaHorarios.filter(h => h.diaSemana === diaSemana);
  const dayMedico  = bloquesMedico.filter(b => b.diaSemana === diaSemana);
  const medicoHasAvailSlots = dayMedico.some(b => !b.esBloqueo);

  for (const slot of TIME_SLOTS) {
    const slotStart = timeToMins(slot);
    const slotEnd   = slotStart + duracion;

    let status: SlotStatus = 'open';

    // Block past time slots when the selected date is today
    if (isToday && slotStart <= nowMins) {
      map.set(slot, 'closed');
      continue;
    }

    // Clinic hours check — slot must start AND end within an open period
    if (dayClinica.length > 0) {
      const openSlot = dayClinica.find(
        h => !h.esBloqueo && timeToMins(h.horaInicio) <= slotStart && timeToMins(h.horaFin) >= slotEnd,
      );
      const blockedSlot = dayClinica.find(
        h => h.esBloqueo && timeToMins(h.horaInicio) < slotEnd && timeToMins(h.horaFin) > slotStart,
      );
      if (!openSlot || blockedSlot) status = 'closed';
    } else if (clinicaHorarios.length > 0) {
      // Clinic has a schedule but no entries for this day → day is closed
      status = 'closed';
    }

    // Doctor availability check
    if (status === 'open' && medicoId && dayMedico.length > 0) {
      const blockedSlot = dayMedico.find(
        b => b.esBloqueo && timeToMins(b.horaInicio) < slotEnd && timeToMins(b.horaFin) > slotStart,
      );
      if (medicoHasAvailSlots) {
        // Doctor defines own hours → slot must start AND end within an available slot
        const availSlot = dayMedico.find(
          b => !b.esBloqueo && timeToMins(b.horaInicio) <= slotStart && timeToMins(b.horaFin) >= slotEnd,
        );
        if (!availSlot || blockedSlot) status = 'closed';
      } else {
        // Only explicit blocks → just block those specific ranges
        if (blockedSlot) status = 'closed';
      }
    }

    if (status === 'open' && medicoId) {
      const slotStartMs = new Date(`${fecha}T${slot}:00`).getTime();
      const slotEndMs   = slotStartMs + duracion * 60_000;
      const hasConflict = citasDelDia.some(c => {
        if (c.estado === 'cancelada') return false;
        if (editingId && c.id === editingId) return false;
        const cS = new Date(c.fechaInicio).getTime();
        const cE = new Date(c.fechaFin).getTime();
        return cS < slotEndMs && cE > slotStartMs;
      });
      if (hasConflict) status = 'conflict';
    }

    map.set(slot, status);
  }
  return map;
}

/* ── TimeSlotPicker ─────────────────────────────────────────────────────── */

function TimeSlotPicker({
  value, onChange, slotStatuses,
}: { value: string; onChange: (s: string) => void; slotStatuses: Map<string, SlotStatus> }) {
  return (
    <div className="grid grid-cols-5 gap-1 max-h-[168px] overflow-y-auto pr-0.5">
      {TIME_SLOTS.map(slot => {
        const status  = slotStatuses.size ? (slotStatuses.get(slot) ?? 'open') : 'open';
        const selected = slot === value;
        const closed   = status === 'closed';
        const conflict = status === 'conflict';
        return (
          <button
            key={slot}
            type="button"
            disabled={closed}
            onClick={() => onChange(slot)}
            className={cn(
              'py-1.5 text-[11px] font-semibold rounded-lg border transition-all text-center leading-none',
              selected && !closed && !conflict && 'border-primary-500 bg-primary-500 text-white',
              selected && conflict && 'border-red-500 bg-red-500 text-white',
              !selected && !closed && !conflict && 'border-gray-200 text-text-primary hover:border-primary-300 hover:bg-primary-50',
              !selected && conflict && 'border-red-200 bg-red-50 text-red-500',
              closed && 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed line-through',
            )}
          >
            {slot}
          </button>
        );
      })}
    </div>
  );
}

/* ── Availability panel ─────────────────────────────────────────────────── */

function AvailabilityPanel({ av }: { av: AvailabilityStatus | null }) {
  if (!av || av.loading) {
    if (av?.loading) {
      return (
        <div className="flex items-center gap-2 text-xs text-text-secondary px-3 py-2 rounded-xl bg-gray-50 border border-gray-100">
          <Loader2 size={12} className="animate-spin flex-shrink-0" />
          Verificando disponibilidad...
        </div>
      );
    }
    return null;
  }

  const rows: Array<{ ok: boolean; label: string; show: boolean }> = [
    {
      show: av.clinicaConfigured,
      ok: av.clinicaOpen,
      label: av.clinicaOpen ? 'Clínica abierta en este horario' : 'La clínica no está disponible en este horario',
    },
    {
      show: av.medicoConfigured,
      ok: av.medicoAvailable,
      label: av.medicoAvailable ? 'Médico disponible' : 'El médico no tiene disponibilidad en este horario',
    },
    {
      show: av.hasConflict,
      ok: false,
      label: 'El médico ya tiene una cita en este horario',
    },
  ].filter(r => r.show);

  if (!rows.length) return null;

  const allOk = rows.every(r => r.ok);

  return (
    <div className={cn(
      'rounded-xl border px-3 py-2.5 space-y-1.5',
      allOk ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50',
    )}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-2">
          {r.ok
            ? <CheckCircle2 size={13} className="text-green-600 flex-shrink-0" />
            : <AlertCircle size={13} className="text-red-600 flex-shrink-0" />
          }
          <span className={cn('text-xs font-medium', r.ok ? 'text-green-800' : 'text-red-800')}>
            {r.label}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── Step 1: Fecha y sala ───────────────────────────────────────────────── */

function Step1({
  state, set, medicos, clinicas, availability, slotStatuses,
}: {
  state: WizardState;
  set: (k: keyof WizardState, v: unknown) => void;
  medicos: any[];
  clinicas: any[];
  availability: AvailabilityStatus | null;
  slotStatuses: Map<string, SlotStatus>;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-text-primary mb-1.5">
            Doctor/a <span className="text-red-500">*</span>
          </label>
          <select
            value={state.medicoId}
            onChange={e => set('medicoId', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">Seleccionar médico</option>
            {medicos.map((m: any) => (
              <option key={m.id} value={m.id}>{m.nombre}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-primary mb-1.5">
            Sala <span className="text-red-500">*</span>
          </label>
          <select
            value={state.clinicaId}
            onChange={e => set('clinicaId', e.target.value)}
            className={FIELD_CLS}
          >
            <option value="">Seleccionar sala</option>
            {clinicas.map((c: any) => (
              <option key={c.id} value={c.id}>{c.nombre}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-text-primary mb-1.5">
            Fecha <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={state.fecha}
            min={format(new Date(), 'yyyy-MM-dd')}
            onChange={e => set('fecha', e.target.value)}
            className={FIELD_CLS}
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-primary mb-1.5">
            Duración
          </label>
          <div className="flex items-center gap-1.5 px-2 py-2 rounded-xl border border-gray-200 bg-white justify-between">
            <button
              type="button"
              onClick={() => set('duracion', Math.max(15, (state.duracion as number) - 15))}
              className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <Minus size={11} />
            </button>
            <span className="text-sm font-bold text-text-primary tabular-nums text-center flex-1">
              {state.duracion} min
            </span>
            <button
              type="button"
              onClick={() => set('duracion', Math.min(240, (state.duracion as number) + 15))}
              className="w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors flex-shrink-0"
            >
              <Plus size={11} />
            </button>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-text-primary mb-1.5">
          Hora <span className="text-red-500">*</span>
        </label>
        <TimeSlotPicker
          value={state.hora}
          onChange={v => set('hora', v)}
          slotStatuses={slotStatuses}
        />
      </div>

      {medicos.length === 0 && (
        <p className="text-xs text-text-secondary">
          Para agregar doctores con cédula ve a{' '}
          <a href="/personal" className="text-primary-600 hover:underline">Configuración › Equipo</a>.
        </p>
      )}

      {state.fecha && state.hora && new Date(`${state.fecha}T${state.hora}:00`) <= new Date() && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 flex items-center gap-2">
          <AlertCircle size={13} className="text-red-600 flex-shrink-0" />
          <span className="text-xs font-medium text-red-800">
            No se pueden registrar citas en fechas u horarios pasados
          </span>
        </div>
      )}

      <AvailabilityPanel av={availability} />
    </div>
  );
}

/* ── Step 2: Paciente ───────────────────────────────────────────────────── */

function Step2({
  state, set, pacientes, loading, clinicaId,
}: {
  state: WizardState;
  set: (k: keyof WizardState, v: unknown) => void;
  pacientes: any[];
  loading?: boolean;
  clinicaId: string;
}) {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [np, setNp] = useState({ nombre: '', apellidoPaterno: '', telefono: '' });

  const filtered = useMemo(() => {
    if (!search.trim()) return pacientes;
    const q = search.toLowerCase();
    return pacientes.filter((p: any) => {
      const name = `${p.nombre ?? ''} ${p.apellidoPaterno ?? ''}`.toLowerCase();
      return name.includes(q) || (p.telefono ?? '').includes(q) || (p.numeroExpediente ?? '').toLowerCase().includes(q);
    });
  }, [pacientes, search]);

  const createMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/pacientes', data).then(r => r.data),
    onSuccess: (pac) => {
      qc.invalidateQueries({ queryKey: ['pacientes-select'] });
      set('pacienteId', pac.id);
      setCreating(false);
      setNp({ nombre: '', apellidoPaterno: '', telefono: '' });
      toast.success('Paciente registrado');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al registrar el paciente')),
  });

  if (creating) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold text-text-primary">Nuevo paciente</p>
          <button
            type="button"
            onClick={() => setCreating(false)}
            className="text-xs text-text-secondary hover:text-text-primary underline"
          >
            Cancelar
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Nombre *</label>
            <input
              autoFocus required
              value={np.nombre}
              onChange={e => setNp(s => ({ ...s, nombre: e.target.value }))}
              className={FIELD_CLS}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Apellido *</label>
            <input
              required
              value={np.apellidoPaterno}
              onChange={e => setNp(s => ({ ...s, apellidoPaterno: e.target.value }))}
              className={FIELD_CLS}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-text-secondary mb-1">Teléfono</label>
          <input
            type="tel"
            value={np.telefono}
            onChange={e => setNp(s => ({ ...s, telefono: e.target.value }))}
            className={FIELD_CLS}
          />
        </div>
        <button
          type="button"
          disabled={!np.nombre || !np.apellidoPaterno || createMutation.isPending}
          onClick={() => createMutation.mutate({ ...np, clinicaId })}
          className="w-full py-2.5 bg-primary-500 text-white text-sm font-medium rounded-xl hover:bg-primary-600 disabled:opacity-40 transition-colors"
        >
          {createMutation.isPending ? 'Registrando...' : 'Registrar paciente'}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary pointer-events-none" />
        <input
          autoFocus
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Nombre, teléfono o expediente..."
          className={cn(FIELD_CLS, 'pl-8')}
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6 gap-2 text-text-secondary">
          <Loader2 size={16} className="animate-spin" />
          <span className="text-sm">Cargando pacientes...</span>
        </div>
      )}

      {!loading && (
      <div className="max-h-52 overflow-y-auto space-y-1 -mx-1 px-1">
        {filtered.slice(0, 12).map((p: any) => (
          <button
            key={p.id}
            type="button"
            onClick={() => set('pacienteId', p.id)}
            className={cn(
              'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left',
              state.pacienteId === p.id
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50',
            )}
          >
            <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600 flex-shrink-0">
              {initials(p.nombre ?? '', p.apellidoPaterno ?? '')}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">
                {p.nombre} {p.apellidoPaterno}
              </p>
              {p.telefono && (
                <p className="text-xs text-text-secondary">{p.telefono}</p>
              )}
            </div>
            {state.pacienteId === p.id && (
              <Check size={14} className="text-primary-500 flex-shrink-0" />
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-4">
            {search ? 'Sin resultados para esa búsqueda' : 'Sin pacientes registrados'}
          </p>
        )}
      </div>
      )}

      <button
        type="button"
        onClick={() => setCreating(true)}
        className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
      >
        <Plus size={14} />
        Crear paciente nuevo
      </button>
    </div>
  );
}

/* ── Step 3: Tratamiento / CIE-11 ───────────────────────────────────────── */

function Step3({
  state, set,
}: {
  state: WizardState;
  set: (k: keyof WizardState, v: unknown) => void;
}) {
  const { data: servicios = [] } = useQuery({
    queryKey: ['servicios'],
    queryFn: () => api.get('/api/servicios').then(r => r.data as any[]),
    staleTime: 60_000,
  });

  const cie11Value: Cie11Result | null = state.cie11Titulo
    ? { id: state.cie11Codigo ?? '', theCode: state.cie11Codigo ?? '', title: state.cie11Titulo }
    : null;

  function handleCie11Change(r: Cie11Result | null) {
    set('cie11Codigo', r?.theCode ?? undefined);
    set('cie11Titulo', r?.title   ?? undefined);
    set('motivo',      r?.title   ?? '');
  }

  function selectServicio(srv: any) {
    if (state.servicioId === srv.id) {
      set('servicioId', undefined);
      set('servicioNombre', undefined);
    } else {
      set('servicioId', srv.id);
      set('servicioNombre', srv.nombre);
    }
  }

  return (
    <div className="space-y-3">
      {/* Servicio */}
      {servicios.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-text-primary mb-1.5">
            Servicio <span className="font-normal text-text-secondary">(opcional)</span>
          </p>
          <div className="max-h-32 overflow-y-auto space-y-1 -mx-1 px-1">
            {servicios.map((srv: any) => (
              <button
                key={srv.id}
                type="button"
                onClick={() => selectServicio(srv)}
                className={cn(
                  'w-full flex items-center justify-between px-3 py-2 rounded-xl border transition-all text-left',
                  state.servicioId === srv.id
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50',
                )}
              >
                <span className={cn('text-sm font-medium', state.servicioId === srv.id ? 'text-primary-700' : 'text-text-primary')}>
                  {srv.nombre}
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-text-secondary">${Number(srv.precio).toLocaleString('es-MX')}</span>
                  {state.servicioId === srv.id && <Check size={13} className="text-primary-500" />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-text-secondary leading-relaxed">
        <span className="font-semibold text-text-primary">Opcional.</span>{' '}
        Busca el diagnóstico o motivo de consulta en CIE-11. Puedes dejarlo en blanco y registrarlo durante la consulta.
      </p>

      {/* CIE-11 search */}
      <Cie11Search
        value={cie11Value}
        onChange={handleCie11Change}
        placeholder="Buscar diagnóstico CIE-11..."
      />

      {/* Tipo */}
      <div className="flex gap-2">
        {(['presencial', 'teleconsulta'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => set('tipo', t)}
            className={cn(
              'flex-1 py-2 rounded-xl border text-xs font-semibold transition-colors capitalize',
              state.tipo === t
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-gray-200 text-text-secondary hover:bg-gray-50',
            )}
          >
            {t === 'presencial' ? 'Presencial' : 'Teleconsulta'}
          </button>
        ))}
      </div>

      {/* Recurrente */}
      <div className={cn(
        'rounded-xl border transition-colors',
        state.esRecurrente ? 'border-primary-200 bg-primary-50/40' : 'border-gray-100',
      )}>
        <label className="flex items-start gap-2.5 cursor-pointer px-3 py-2.5 hover:bg-gray-50/50 transition-colors rounded-xl">
          <input
            type="checkbox"
            checked={state.esRecurrente}
            onChange={e => {
              set('esRecurrente', e.target.checked);
              if (!e.target.checked) set('recurrencia', '');
            }}
            className="mt-0.5 w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-300 flex-shrink-0"
          />
          <div>
            <p className="text-xs font-semibold text-text-primary leading-tight">Cita recurrente</p>
            <p className="text-[10px] text-text-secondary mt-0.5">
              Útil para ortodoncia, seguimientos u otras visitas periódicas.
            </p>
          </div>
        </label>

        {state.esRecurrente && (
          <div className="px-3 pb-3 space-y-1.5">
            <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-2">
              Frecuencia <span className="text-red-500">*</span>
            </p>
            <div className="grid grid-cols-2 gap-1.5">
              {RECURRENCIA_OPTS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('recurrencia', opt.value)}
                  className={cn(
                    'flex flex-col items-start px-3 py-2 rounded-xl border text-left transition-all',
                    state.recurrencia === opt.value
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                  )}
                >
                  <span className={cn(
                    'text-xs font-semibold leading-tight',
                    state.recurrencia === opt.value ? 'text-primary-700' : 'text-text-primary',
                  )}>
                    {opt.label}
                  </span>
                  <span className="text-[10px] text-text-secondary mt-0.5">{opt.desc}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Notas */}
      <div>
        <label className="block text-xs font-semibold text-text-primary mb-1.5">
          Notas <span className="font-normal text-text-secondary">(opcional)</span>
        </label>
        <textarea
          value={state.notas}
          onChange={e => set('notas', e.target.value)}
          rows={2}
          placeholder="Observaciones, materiales especiales..."
          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
        />
      </div>
    </div>
  );
}

function TratRow({
  selected, title, subtitle, onClick,
}: { selected: boolean; title: string; subtitle?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left',
        selected
          ? 'border-primary-500 bg-primary-50'
          : 'border-gray-100 hover:border-gray-300 hover:bg-gray-50',
      )}
    >
      <div className={cn(
        'w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5',
        selected ? 'border-primary-500 bg-primary-500' : 'border-gray-300',
      )}>
        {selected && <Check size={9} className="text-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className={cn('text-sm font-semibold leading-tight', selected ? 'text-primary-700' : 'text-text-primary')}>
          {title}
        </p>
        {subtitle && <p className="text-xs text-text-secondary mt-0.5">{subtitle}</p>}
      </div>
    </button>
  );
}

/* ── Step 4: Resumen ────────────────────────────────────────────────────── */

function Step4({
  state, medicos, clinicas, pacientes, servicios,
}: {
  state: WizardState;
  medicos: any[];
  clinicas: any[];
  pacientes: any[];
  servicios: any[];
}) {
  const pac = pacientes.find((p: any) => p.id === state.pacienteId);
  const med = medicos.find((m: any) => m.id === state.medicoId);
  const cli = clinicas.find((c: any) => c.id === state.clinicaId);

  const start = new Date(`${state.fecha}T${state.hora}:00`);
  const end = addMinutes(start, state.duracion);

  const fechaLabel = format(start, "d 'de' MMMM yyyy", { locale: es });
  const horaLabel  = `${state.hora} – ${format(end, 'HH:mm')} (${state.duracion} min)`;
  const recurrenciaLabel = RECURRENCIA_OPTS.find(o => o.value === state.recurrencia)?.label ?? '';

  return (
    <div className="space-y-4">
      <p className="text-xs text-text-secondary">Revisa los datos antes de crear la cita.</p>

      <div className="rounded-2xl border border-primary-100 bg-primary-50/30 p-4 space-y-3">
        {/* Patient */}
        {pac && (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-sm font-bold text-primary-700 flex-shrink-0">
              {initials(pac.nombre ?? '', pac.apellidoPaterno ?? '')}
            </div>
            <div>
              <p className="text-sm font-bold text-text-primary">
                {pac.nombre} {pac.apellidoPaterno}
              </p>
              {pac.telefono && <p className="text-xs text-text-secondary">{pac.telefono}</p>}
            </div>
          </div>
        )}

        <div className="border-t border-primary-100/60" />

        <div className="grid grid-cols-2 gap-y-3 gap-x-4">
          <SummaryField label="Fecha" value={fechaLabel} className="capitalize" />
          <SummaryField label="Hora" value={horaLabel} />
          <SummaryField label="Doctor/a" value={med?.nombre ?? '—'} />
          <SummaryField label="Sala" value={cli?.nombre ?? '—'} />
        </div>

        <div className="border-t border-primary-100/60" />

        <div className="grid grid-cols-2 gap-y-3 gap-x-4">
          {state.servicioId && (() => {
            const srv = servicios.find((s: any) => s.id === state.servicioId);
            const val = srv
              ? `${srv.nombre} · $${Number(srv.precio).toLocaleString('es-MX')}`
              : state.servicioNombre ?? '';
            return val ? <SummaryField label="Servicio" value={val} /> : null;
          })()}
          <SummaryField
            label="CIE-11"
            value={state.cie11Titulo
              ? [state.cie11Codigo, state.cie11Titulo].filter(Boolean).join(' · ')
              : 'Sin definir'}
          />
          <SummaryField label="Tipo" value={state.tipo === 'presencial' ? 'Presencial' : 'Teleconsulta'} />
        </div>

        {(state.esRecurrente || state.notas) && (
          <>
            <div className="border-t border-primary-100/60" />
            {state.esRecurrente && (
              <p className="text-xs text-text-secondary flex items-center gap-1.5">
                <Check size={12} className="text-primary-500 flex-shrink-0" />
                Cita recurrente · {recurrenciaLabel}
              </p>
            )}
            {state.notas && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-0.5">Notas</p>
                <p className="text-xs text-text-primary">{state.notas}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SummaryField({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-wider text-text-secondary mb-0.5">{label}</p>
      <p className={cn('text-sm font-medium text-text-primary', className)}>{value}</p>
    </div>
  );
}

/* ── Main Dialog ────────────────────────────────────────────────────────── */

export function CitaFormDialog({
  open, onOpenChange, pacientes, medicos, clinicas,
  defaultValues, editingCita, onSubmit, isSubmitting,
}: CitaFormDialogProps) {
  const navigate   = useNavigate();
  const isEditing  = !!editingCita;
  const puedeConsulta = isEditing && !['programada', 'cancelada'].includes(editingCita?.estado);
  const [step, setStep] = useState<Step>(1);
  const [state, setState] = useState<WizardState>(INIT);

  const { data: tratamientos = [] } = useQuery({
    queryKey: ['tratamientos'],
    queryFn: () => api.get('/api/tratamientos').then(r => r.data).catch(() => []),
  });

  const { data: servicios = [] } = useQuery({
    queryKey: ['servicios'],
    queryFn: () => api.get('/api/servicios').then(r => r.data as any[]).catch(() => []),
    staleTime: 60_000,
  });

  /* ── Availability queries (Step 1 only) ──────────────────────────────── */

  // state.clinicaId = consultorio ID; consultorios have .clinicaId = parent Clinica ID
  const selectedConsultorio = (clinicas as any[]).find(c => c.id === state.clinicaId);
  const effectiveClinicaId  = selectedConsultorio?.clinicaId ?? '';

  const { data: clinicaHorarios = [], isLoading: loadingHorarios } = useQuery({
    queryKey: ['clinica-horarios', effectiveClinicaId],
    queryFn: () => api.get(`/api/clinicas/${effectiveClinicaId}/horarios`).then(r => r.data).catch(() => []),
    enabled: !!effectiveClinicaId,
    staleTime: 60_000,
  });

  const { data: bloquesMedico = [], isLoading: loadingBloques } = useQuery({
    queryKey: ['bloques-horario-medico', state.medicoId],
    queryFn: () => api.get(`/api/bloques-horario?medicoId=${state.medicoId}`).then(r => r.data).catch(() => []),
    enabled: !!state.medicoId,
    staleTime: 60_000,
  });

  const { data: citasDelDia = [], isLoading: loadingCitas } = useQuery({
    queryKey: ['citas-dia', state.medicoId, state.fecha],
    queryFn: () =>
      api.get(`/api/citas?medicoId=${state.medicoId}&fechaInicio=${state.fecha}T00:00:00&fechaFin=${state.fecha}T23:59:59`)
        .then(r => r.data).catch(() => []),
    enabled: !!state.medicoId && !!state.fecha,
    staleTime: 30_000,
  });

  const { data: pacientesMedico = [], isLoading: loadingPacientes } = useQuery({
    queryKey: ['pacientes-medico', state.medicoId],
    queryFn: () => api.get(`/api/pacientes?medicoId=${state.medicoId}`).then(r => r.data),
    enabled: !!state.medicoId,
    staleTime: 60_000,
  });

  const availability = useMemo((): AvailabilityStatus | null => {
    if (!state.fecha || !state.hora || !state.clinicaId || !state.medicoId) return null;

    const loading = loadingHorarios || loadingBloques || loadingCitas;

    const apptDate = new Date(`${state.fecha}T${state.hora}:00`);
    const apptStart = timeToMins(state.hora);
    const apptEnd = apptStart + state.duracion;
    const diaSemana = ES_DAYS[apptDate.getDay()];

    const allClinicaHorarios = clinicaHorarios as any[];
    const dayClinicaHorarios = allClinicaHorarios.filter(h => h.diaSemana === diaSemana);
    const clinicaConfigured = allClinicaHorarios.length > 0;
    let clinicaOpen = true;
    if (clinicaConfigured) {
      if (dayClinicaHorarios.length === 0) {
        clinicaOpen = false;
      } else {
        const openSlot = dayClinicaHorarios.find(
          h => !h.esBloqueo && timeToMins(h.horaInicio) <= apptStart && timeToMins(h.horaFin) >= apptEnd,
        );
        const blockedSlot = dayClinicaHorarios.find(
          h => h.esBloqueo && timeToMins(h.horaInicio) < apptEnd && timeToMins(h.horaFin) > apptStart,
        );
        clinicaOpen = !!openSlot && !blockedSlot;
      }
    }

    const dayBloques = (bloquesMedico as any[]).filter(b => b.diaSemana === diaSemana);
    const medicoConfigured = dayBloques.length > 0;
    const medicoHasAvailSlots = dayBloques.some(b => !b.esBloqueo);
    let medicoAvailable = true;

    if (medicoConfigured) {
      const blockedSlot = dayBloques.find(
        b => b.esBloqueo && timeToMins(b.horaInicio) < apptEnd && timeToMins(b.horaFin) > apptStart,
      );
      if (medicoHasAvailSlots) {
        // Doctor defines own hours → must be inside an available slot and not blocked
        const availSlot = dayBloques.find(
          b => !b.esBloqueo && timeToMins(b.horaInicio) <= apptStart && timeToMins(b.horaFin) >= apptEnd,
        );
        medicoAvailable = !!availSlot && !blockedSlot;
      } else {
        // Only explicit blocks configured → inherit clinic availability, just block those ranges
        medicoAvailable = clinicaOpen && !blockedSlot;
      }
    } else if (clinicaConfigured) {
      medicoAvailable = clinicaOpen;
    }

    const apptStartMs = apptDate.getTime();
    const apptEndMs = apptStartMs + state.duracion * 60_000;
    const hasConflict = (citasDelDia as any[]).some(c => {
      if (c.estado === 'cancelada') return false;
      if (editingCita?.id && c.id === editingCita.id) return false;
      const cStart = new Date(c.fechaInicio).getTime();
      const cEnd = new Date(c.fechaFin).getTime();
      return cStart < apptEndMs && cEnd > apptStartMs;
    });

    return { loading, clinicaConfigured, clinicaOpen, medicoConfigured, medicoAvailable, hasConflict };
  }, [
    state.fecha, state.hora, state.duracion, state.clinicaId, state.medicoId,
    clinicaHorarios, bloquesMedico, citasDelDia, loadingHorarios, loadingBloques, loadingCitas,
  ]);

  const slotStatuses = useMemo(
    () => computeSlotStatuses(
      state.fecha, state.duracion, state.clinicaId, state.medicoId,
      clinicaHorarios as any[], bloquesMedico as any[], citasDelDia as any[],
      editingCita?.id,
    ),
    [state.fecha, state.duracion, state.clinicaId, state.medicoId,
      clinicaHorarios, bloquesMedico, citasDelDia, editingCita?.id],
  );

  useEffect(() => {
    if (open) {
      setStep(1);
      setState(editingCita ? parseCita(editingCita) : parseDefaults(defaultValues));
    }
  }, [open]);

  function set(k: keyof WizardState, v: unknown) {
    setState(s => ({ ...s, [k]: v }));
  }

  function canProceed(): boolean {
    switch (step) {
      case 1: {
        if (!(state.fecha && state.hora && state.medicoId && state.clinicaId)) return false;
        if (new Date(`${state.fecha}T${state.hora}:00`) <= new Date()) return false;
        if (!availability || availability.loading) return !availability;
        if (availability.hasConflict) return false;
        if (availability.medicoConfigured && !availability.medicoAvailable) return false;
        if (availability.clinicaConfigured && !availability.clinicaOpen) return false;
        return true;
      }
      case 2: return !!state.pacienteId;
      case 3: return !state.esRecurrente || !!state.recurrencia;
      case 4: return true;
    }
  }

  function handleSubmit() {
    const start = new Date(`${state.fecha}T${state.hora}:00`);
    const end = addMinutes(start, state.duracion);
    onSubmit({
      id:              editingCita?.id,
      pacienteId:      state.pacienteId,
      medicoId:        state.medicoId,
      clinicaId:       effectiveClinicaId || state.clinicaId,
      fechaInicio:     start.toISOString(),
      fechaFin:        end.toISOString(),
      tipo:            state.tipo,
      motivo:          state.motivo || '',
      codigoCie11:     state.cie11Codigo || undefined,
      tituloCie11:     state.cie11Titulo || undefined,
      notas:           state.notas || undefined,
      esRecurrente:    state.esRecurrente,
      recurrencia:     state.esRecurrente ? state.recurrencia || undefined : undefined,
      servicioId:      state.servicioId || undefined,
      servicioNombre:  state.servicioNombre || undefined,
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
        <Dialog.Content className="fixed inset-x-0 bottom-0 md:inset-x-auto md:left-1/2 md:top-1/2 md:bottom-auto md:-translate-x-1/2 md:-translate-y-1/2 z-50 bg-white rounded-t-3xl md:rounded-2xl shadow-2xl w-full md:max-w-xl px-4 pt-2 pb-6 md:p-6 focus:outline-none max-h-[92vh] overflow-y-auto">

          {/* Drag handle — mobile only */}
          <div className="flex justify-center mb-3 md:hidden">
            <div className="w-10 h-1 rounded-full bg-gray-200" />
          </div>

          {/* Header */}
          <div className="flex items-start justify-between mb-5">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-0.5">
                PASO {step} DE 4
              </p>
              <Dialog.Title className="text-xl font-bold text-text-primary leading-tight">
                {isEditing ? 'Editar cita' : 'Nueva cita'}
              </Dialog.Title>
            </div>
            <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors mt-1">
              <X size={18} />
            </Dialog.Close>
          </div>

          {/* Step indicator */}
          <StepIndicator current={step} onGoTo={setStep} />

          {/* Step content */}
          <div className="min-h-[220px]">
            {step === 1 && (
              <Step1 state={state} set={set} medicos={medicos} clinicas={clinicas} availability={availability} slotStatuses={slotStatuses} />
            )}
            {step === 2 && (
              <Step2 state={state} set={set} pacientes={pacientesMedico} loading={loadingPacientes} clinicaId={effectiveClinicaId || state.clinicaId} />
            )}
            {step === 3 && (
              <Step3 state={state} set={set} />
            )}
            {step === 4 && (
              <Step4
                state={state}
                medicos={medicos}
                clinicas={clinicas}
                pacientes={pacientesMedico}
                servicios={servicios}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2 mt-6 pt-4 border-t border-gray-100">
            <Dialog.Close className="px-4 py-2.5 rounded-xl text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors">
              Cancelar
            </Dialog.Close>
            {puedeConsulta && (
              <Dialog.Close asChild>
                <button
                  type="button"
                  onClick={() => navigate(`/consultas/${editingCita.id}`)}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-primary-200 bg-primary-50 text-primary-700 text-sm font-semibold hover:bg-primary-100 transition-colors"
                >
                  <Stethoscope size={14} /> Abrir consulta
                </button>
              </Dialog.Close>
            )}
            <div className="flex-1" />
            {step > 1 && (
              <button
                type="button"
                onClick={() => setStep((step - 1) as Step)}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors"
              >
                ← Atrás
              </button>
            )}
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep((step + 1) as Step)}
                disabled={!canProceed()}
                className="px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Continuar →
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors"
              >
                <Check size={14} />
                {isSubmitting ? (isEditing ? 'Guardando...' : 'Creando...') : (isEditing ? 'Guardar cambios' : 'Crear cita')}
              </button>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
